import { Fragment, useMemo } from 'react';

import {
  FilterAppliedProps,
  FilterComparer,
  FilterConfig,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconStatus } from '@components/icons/status';

import { useStore } from 'models/Provider';

import { FilterActionButtons } from './filter-action-buttons';
import { FilterComparerMenu } from './filter-comparer-menu';

const STATUS_COMPARERS: Array<FilterComparer> = ['is', 'not_is', 'any_of', 'none_of'];

function FilterApplicationStatusApplied({ data }: FilterAppliedProps) {
  const { t } = useStore();

  const valueLabel = Array.isArray(data.value)
    ? data.value.map((label) => t(`applicants.status.${label}`))
    : t(`applicants.status.${data.value as string}`);

  return (
    <Filters.AppliedFilter
      icon={<IconStatus />}
      category={t('applicants.status.title')}
      value={valueLabel}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

function FilterApplicationStatusMenu() {
  const { t } = useStore();
  const { state, actions, isMultiComparer, hasValue } = useCurrentFilter();

  const filterValueItems = ['active', 'hired', 'rejected', 'retracted'].map((item) => ({
    value: item,
    label: t('applicants.status.' + item),
  }));

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.status.title')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      {state.comparer && (
        <Filters.ListItem
          heading
          label={t('comparers.' + state.comparer).replace('...', '')}
          icon={<IconArrowLeft />}
          onClick={actions.unselectComparer}
        />
      )}

      <Filters.ListSeparator />

      {!state.comparer && (
        <FilterComparerMenu
          comparers={STATUS_COMPARERS}
          onComparerSelect={actions.selectComparer}
        />
      )}

      {state.comparer && (
        <Fragment>
          <Filters.FilterValueMenu
            items={filterValueItems}
            isMulti={isMultiComparer}
            onSetValue={actions.selectValue}
            state={state}
          />
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}
    </Filters.List>
  );
}

export function useFilterApplicationStatus(): FilterConfig {
  const { t } = useStore();

  return useMemo(() => {
    return {
      category: 'status',
      label: t('applicants.status.title'),
      icon: <IconStatus />,
      AppliedFilter: FilterApplicationStatusApplied,
      Menu: FilterApplicationStatusMenu,
      toQueryData: (filter) => {
        switch (filter.comparer) {
          case 'is':
            return { status__term: filter.value };
          case 'any_of':
            const value = Array.isArray(filter.value) ? filter.value.join('__') : filter.value;
            return { status__terms: value };
          case 'not_is':
          case 'none_of':
            return { status__exclude: filter.value };
          default:
            return {};
        }
      },
    };
  }, [t]);
}
